import React, { useEffect, useState, useRef } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { thumbUrl } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { QuantityInput } from "@/components/QuantityInput";
import { toast } from "sonner";
import { DogIcon as Dog, BoneIcon as Bone, TShirtIcon as TShirt, GraduationCapIcon as GraduationCap, ArrowRightIcon as ArrowRight, SparkleIcon as Sparkle, StorefrontIcon as Storefront, CheckCircleIcon as CheckCircle, EnvelopeSimpleIcon as EnvelopeSimple, PaintBrushIcon as PaintBrush, GearIcon as Gear } from "@phosphor-icons/react";
import { NoiseOverlay, GoldDivider, LuxuryCard } from "@/components/luxury";

/**
 * `/armstrong` — the Armstrong family's all-in-one storefront.
 *
 * Public page that consolidates three businesses under one roof:
 *   1. Dog training subscriptions (contact-to-onboard model — admin
 *      generates a private Stripe payment link per client)
 *   2. Dog food catalog (native Stripe Checkout, local delivery)
 *   3. Merch synced live from the family Printify shop
 *
 * Everything is fetched in one round-trip from `GET /armstrong/storefront`.
 */

const fmtUSD = (cents) =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });

export default function Armstrong() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const justOrdered = searchParams.get("order_id");

  useEffect(() => {
    api.get("/armstrong/storefront")
      .then((r) => setData(r.data))
      .catch(() => setData({ food: [], merch: [], training: {} }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (justOrdered) {
      toast.success("Order placed! Check your email for the receipt.");
    }
  }, [justOrdered]);

  // Once the founder has run the Armstrong → Dog Shop migration tool,
  // `/armstrong` becomes a hard redirect to the seller's new storefront.
  // Skip the redirect for /armstrong/return so post-checkout success
  // pages from legacy orders still resolve cleanly.
  const migratedSlug = data?.migration?.store_slug;
  if (migratedSlug && !justOrdered) {
    return <Navigate to={`/store/${migratedSlug}`} replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope">
      <Helmet>
        <title>Armstrong — training, food & merch from the Armstrong family</title>
        <meta name="description" content="Dog training subscriptions, premium dog food, and Armstrong K9 Registry merch — all in one place." />
      </Helmet>

      {/* Header — same translucent overlay treatment as the main landing
          page, so visitors landing here from social ads feel they're on
          the same brand. */}
      <header className="absolute top-0 inset-x-0 z-40 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="armstrong-brand">
            <img src="/logo-256.png" alt="" className="h-9 w-9 rounded-full object-contain shadow-[0_0_20px_rgba(201,162,39,0.25)]" />
            <span className="font-spectral text-base tracking-tight whitespace-nowrap hidden sm:inline text-white">
              Armstrong K9 Registry
            </span>
          </Link>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Link to="/admin/armstrong">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full text-[13px] text-white/85 hover:text-white hover:bg-white/10"
                  data-testid="armstrong-admin-console-btn"
                >
                  <Gear size={14} weight="duotone" className="mr-1.5" /> Admin console
                </Button>
              </Link>
            )}
            {user ? (
              <>
                <Link to="/marketplace">
                  <Button variant="ghost" size="sm" className="rounded-full text-[13px] text-white/80 hover:text-white hover:bg-white/10 hidden sm:inline-flex">
                    Marketplace
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button size="sm" className="rounded-full bg-white text-[#0E0E0E] hover:bg-gold hover:text-[#0E0E0E] text-[13px] font-medium" data-testid="armstrong-go-dashboard">
                    My dashboard
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/marketplace">
                  <Button variant="ghost" size="sm" className="rounded-full text-[13px] text-white/80 hover:text-white hover:bg-white/10">
                    Marketplace
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="sm" className="rounded-full bg-white text-[#0E0E0E] hover:bg-gold text-[13px] font-medium">
                    Sign in
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-white" data-testid="armstrong-hero">
        <NoiseOverlay opacity={0.08} />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(201,162,39,0.28), transparent 50%), radial-gradient(circle at 15% 80%, rgba(193,39,45,0.18), transparent 55%)",
          }}
        />
        <div className="pointer-events-none absolute inset-0 bg-vignette-dark" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-4 flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> The Armstrong family · everything in one place
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            Training, food, and merch — from our family to yours.
          </h1>
          <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-white/85 max-w-2xl">
            One page for everything the Armstrong family does: monthly
            dog training plans, premium dog food we deliver locally, and
            apparel printed on demand. No more Zelle, Square, and Venmo
            scavenger hunts.
          </p>
          <div className="mt-9 flex items-center gap-3 flex-wrap">
            <a href="#training">
              <Button className="rounded-full bg-gold-gradient text-ink font-semibold h-12 px-7 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0">
                <GraduationCap size={16} weight="duotone" className="mr-2" /> Training plans
              </Button>
            </a>
            <a href="#food">
              <Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-ink h-12 px-6">
                <Bone size={16} weight="duotone" className="mr-2" /> Order food
              </Button>
            </a>
            <a href="#merch">
              <Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-ink h-12 px-6">
                <TShirt size={16} weight="duotone" className="mr-2" /> Shop merch
              </Button>
            </a>
            <a href="#custom" data-testid="hero-custom-cta">
              <Button variant="outline" className="rounded-full border-gold-light/60 bg-gold-light/10 text-gold-light hover:bg-gold-gradient hover:text-ink hover:border-transparent h-12 px-6">
                <PaintBrush size={16} weight="duotone" className="mr-2" /> Custom items
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CUSTOM ORDERS — first-class entry above the fold */}
      <CustomOrdersHeroStrip />

      {/* MIGRATION BANNER — when admin migrated the food catalog to a
          Dog Shop seller storefront, every shopper landing here gets a
          loud redirect card so they end up on the live store, not on
          this archived page. */}
      {data?.migration?.store_slug && (
        <MigrationBanner slug={data.migration.store_slug} />
      )}

      {/* TRAINING (or contact card after migration) */}
      {data?.migration?.store_slug ? (
        <TrainingContactCard />
      ) : (
        <section id="training" className="relative bg-white py-20 md:py-24" data-testid="armstrong-training-section">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
              Dog training
            </p>
            <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1]">
              {data?.training?.headline || "Personalized dog training, billed monthly"}
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[#3a3a3a] max-w-lg">
              {data?.training?.blurb}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-[#3a3a3a]">
              {[
                "Custom plan and monthly price for your dog",
                "Stripe auto-renews each month — cancel any time",
                "No prorated refunds — service continues through the paid period",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-gold-dark mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3 flex-wrap items-center">
              <Link to="/contact?subject=Dog%20training%20inquiry">
                <Button className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6" data-testid="training-contact-btn">
                  <EnvelopeSimple size={16} className="mr-2" /> Request a plan & price
                </Button>
              </Link>
              <ManageSubscriptionTrigger />
            </div>
          </div>
          <LuxuryCard className="p-8">
            <GraduationCap size={32} weight="duotone" className="text-gold-dark mb-4" />
            <h3 className="font-spectral text-2xl mb-3">How it works</h3>
            <ol className="space-y-3 text-sm text-[#3a3a3a]">
              <li><strong>1. Tell us about your dog.</strong> Goals, age, current behavior, schedule.</li>
              <li><strong>2. We design a plan + monthly price.</strong> Built around what your dog actually needs.</li>
              <li><strong>3. You get a private payment link.</strong> Stripe billing auto-renews monthly. Cancel any time from your inbox.</li>
              <li><strong>4. We get to work.</strong> Sessions begin within 7 days of your first payment.</li>
            </ol>
          </LuxuryCard>
        </div>
      </section>
      )}

      {/* FOOD */}
      {!data?.migration?.store_slug && (
      <section id="food" className="relative bg-cream py-20 md:py-24" data-testid="armstrong-food-section">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-10">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
                Dog food · local delivery or pickup
              </p>
              <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1] max-w-2xl">
                Premium dog food. Delivered to your door, or pick it up.
              </h2>
            </div>
            <p className="text-sm text-[#6B6B6B] max-w-xs">
              Pre-order and pay online. We hand-deliver locally — or pick up at our Anderson, CA location.
            </p>
          </div>
          {loading ? (
            <p className="text-sm text-[#6B6B6B]">Loading catalog…</p>
          ) : (data?.food?.length || 0) === 0 ? (
            <LuxuryCard className="p-10 text-center">
              <Bone size={32} weight="duotone" className="text-gold-dark mx-auto mb-3" />
              <h3 className="font-spectral text-xl mb-2">Catalog coming soon</h3>
              <p className="text-sm text-[#6B6B6B]">We're adding products this week. Check back shortly.</p>
            </LuxuryCard>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.food.map((p) => (
                <FoodCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* MERCH */}
      {!data?.migration?.store_slug && (
      <section id="merch" className="relative bg-white py-20 md:py-24" data-testid="armstrong-merch-section">
        <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-[1fr_minmax(280px,360px)] gap-6 md:gap-10 items-stretch mb-10">
            <div className="flex flex-col justify-end">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3">
                Armstrong apparel
              </p>
              <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.1] max-w-2xl">
                Wear the Armstrong K9 colors.
              </h2>
              <p className="text-sm text-[#6B6B6B] mt-3 max-w-md">
                Printed on demand. {data?.merch_count ? `${data.merch_count} designs` : ""} live now — shipped from our print partner straight to you.
              </p>
            </div>
            <PermanentCustomOrdersCard customCount={data?.custom_count || 0} />
          </div>
          {/* Personalization callout — only when ≥1 merch item is flagged customizable, gives the
              dedicated form CTA in-context with the grid. The persistent card above covers the
              empty case. */}
          {(data?.custom_count || 0) > 0 && (
            <CustomMerchCallout count={data.custom_count} />
          )}
          {loading ? (
            <p className="text-sm text-[#6B6B6B]">Loading merch…</p>
          ) : (data?.merch?.length || 0) === 0 ? (
            <LuxuryCard className="p-10 text-center">
              <TShirt size={32} weight="duotone" className="text-gold-dark mx-auto mb-3" />
              <h3 className="font-spectral text-xl mb-2">Merch syncing…</h3>
              <p className="text-sm text-[#6B6B6B]">Products will appear here as soon as we've published them.</p>
            </LuxuryCard>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {data.merch.slice(0, 24).map((p) => (
                <MerchCard key={p.printify_id} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* CTA strip */}
      <section className="bg-ink text-white relative overflow-hidden" data-testid="armstrong-cta">
        <NoiseOverlay opacity={0.07} />
        <div className="pointer-events-none absolute inset-0" style={{
          background: "radial-gradient(circle at 50% 50%, rgba(201,162,39,0.22), transparent 60%)",
        }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
          <GoldDivider variant="ornament" className="max-w-xs mx-auto mb-8" />
          <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight">
            Questions? Talk to the Armstrong family.
          </h2>
          <p className="mt-5 text-white/80 max-w-xl mx-auto leading-relaxed">
            We answer every message ourselves — usually within a day.
          </p>
          <Link to="/contact" className="inline-block mt-8">
            <Button className="rounded-full bg-gold-gradient text-ink font-semibold h-12 px-7 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0" data-testid="armstrong-contact-btn">
              Contact us <ArrowRight size={16} weight="bold" className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="bg-white border-t border-[#EAEAEA] py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6 flex-wrap text-sm text-[#6B6B6B]">
          <div className="flex items-center gap-2">
            <img src="/logo-256.png" alt="" className="h-6 w-6 rounded-full object-contain" />
            <span>© {new Date().getFullYear()} Armstrong K9 Registry</span>
          </div>
          <nav className="flex items-center gap-5 flex-wrap">
            <Link to="/legal/terms" className="hover:text-ink">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/legal/refund" className="hover:text-ink">Refunds</Link>
            <Link to="/contact" className="hover:text-ink">Contact</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

// ============================================================ MigrationBanner
/**
 * Renders when `armstrong_settings.migrated_to_slug` is set — i.e. the
 * food catalog has been moved off the admin/platform account onto a
 * Dog Shop multi-tenant seller storefront. Funnels traffic to the new
 * URL so this page stops being a checkout surface entirely.
 */
function MigrationBanner({ slug }) {
  return (
    <section className="relative bg-ink text-white py-12 md:py-16" data-testid="armstrong-migration-banner">
      <NoiseOverlay opacity={0.07} />
      <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-3 flex items-center justify-center gap-1.5">
          <Storefront size={12} weight="duotone" /> We&rsquo;ve moved · independent seller
        </p>
        <h2 className="font-spectral font-light text-2xl md:text-4xl tracking-tight">
          Food and merch now live at our new shop.
        </h2>
        <p className="mt-4 text-white/80 text-[14px] md:text-[15px] leading-relaxed max-w-xl mx-auto">
          Armstrong Supreme Team is now an independent seller account on
          Armstrong K9 Registry. Same family, same products &mdash; just under
          its own merchant of record so the Registry stays a neutral platform.
        </p>
        <Link to={`/store/${slug}`} className="inline-block mt-7" data-testid="armstrong-migration-cta">
          <Button className="rounded-full bg-gold-gradient text-ink font-semibold h-12 px-7 shadow-[0_8px_24px_rgba(201,162,39,0.35)] border-0">
            <Storefront size={16} weight="duotone" className="mr-2" /> Shop at /store/{slug}
            <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  );
}

// ============================================================ TrainingContactCard
/**
 * Replaces the Training subscription section once Armstrong migrates
 * to an independent seller storefront. Training services can&rsquo;t be sold
 * via The Dog Shop (see seller-agreement §3 — professional services
 * carry liability tails marketplaces can&rsquo;t cover), so we route
 * inquiries straight to the family&rsquo;s phone + email.
 */
function TrainingContactCard() {
  return (
    <section id="training" className="relative bg-white py-16 md:py-20" data-testid="armstrong-training-contact-section">
      <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3 text-center">
          Training &amp; other services
        </p>
        <h2 className="font-spectral font-light text-2xl md:text-4xl tracking-tight leading-[1.1] text-center max-w-xl mx-auto">
          For training, boarding, and other dog services &mdash; reach the Armstrong family directly.
        </h2>
        <p className="mt-4 text-[14px] text-[#3a3a3a] leading-relaxed text-center max-w-lg mx-auto">
          Professional dog services aren&rsquo;t booked through the Registry storefront.
          Contact the team to set up a custom plan and price.
        </p>
        <LuxuryCard className="mt-8 p-7 md:p-9">
          <div className="grid sm:grid-cols-2 gap-6">
            <a
              href="tel:+15105659257"
              className="group flex items-center gap-3 rounded-xl border border-[#EAEAEA] hover:border-gold-dark hover:bg-cream/60 transition-all p-4"
              data-testid="armstrong-training-phone"
            >
              <span className="p-3 rounded-full bg-cream group-hover:bg-white">
                <GraduationCap size={20} weight="duotone" className="text-gold-dark" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">Call or text</p>
                <p className="font-spectral text-lg text-ink leading-tight">(510) 565-9257</p>
              </div>
            </a>
            <a
              href="mailto:armstrongsupremeteamk9@gmail.com"
              className="group flex items-center gap-3 rounded-xl border border-[#EAEAEA] hover:border-gold-dark hover:bg-cream/60 transition-all p-4"
              data-testid="armstrong-training-email"
            >
              <span className="p-3 rounded-full bg-cream group-hover:bg-white">
                <EnvelopeSimple size={20} weight="duotone" className="text-gold-dark" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">Email</p>
                <p className="font-tabular text-[13px] text-ink leading-tight truncate">
                  armstrongsupremeteamk9@gmail.com
                </p>
              </div>
            </a>
          </div>
          <p className="text-[11px] text-[#9A9A9A] text-center mt-5 leading-relaxed">
            Armstrong K9 Registry doesn&rsquo;t book, broker, or facilitate
            these services. All bookings, contracts, and payment go directly
            between you and the Armstrong family.
          </p>
        </LuxuryCard>
      </div>
    </section>
  );
}



// ============================================================ ManageSubscriptionTrigger
/**
 * Tiny "Manage / cancel subscription" button + dialog. Existing
 * training clients click this, type the email they paid with, and we
 * route them to Stripe's hosted Billing Portal where they can cancel
 * auto-renewal, update card, or download invoices themselves — no
 * Armstrong account or login required.
 */
function ManageSubscriptionTrigger() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/armstrong/training/customer-portal", {
        email,
        origin_url: webOrigin(),
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open portal");
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[13px] text-[#6B6B6B] hover:text-ink underline underline-offset-4 decoration-[#D5D5D5] hover:decoration-ink transition-colors"
        data-testid="manage-sub-trigger"
      >
        Manage or cancel my subscription
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="manage-sub-dialog">
          <DialogHeader>
            <DialogTitle className="font-spectral text-xl">Manage your subscription</DialogTitle>
            <DialogDescription>
              We&rsquo;ll send you to Stripe&rsquo;s secure portal where you can update your card, see invoices, or cancel auto-renewal.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={go} className="space-y-3">
            <div>
              <Label className="text-xs">Email you used to pay</Label>
              <Input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="manage-sub-email"
              />
            </div>
            <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
              Cancellation stops future billing immediately. We do not issue prorated refunds — your access continues until the end of the current paid period.
            </p>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-ink hover:bg-crimson text-white" data-testid="manage-sub-submit">
              {busy ? "Opening…" : "Open Stripe portal"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================== FoodCard
function FoodCard({ product }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <LuxuryCard interactive className="p-5 flex flex-col" data-testid={`food-card-${product.id}`}>
        {product.photo_url ? (
          <img src={thumbUrl(product.photo_url) || product.photo_url} alt={product.name} className="w-full h-44 object-cover rounded-lg mb-4" />
        ) : (
          <div className="w-full h-44 rounded-lg bg-cream flex items-center justify-center mb-4">
            <Bone size={42} weight="duotone" className="text-gold" />
          </div>
        )}
        <h3 className="font-spectral text-lg tracking-tight mb-1">{product.name}</h3>
        {product.bag_size && (
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold-dark mb-2">{product.bag_size}</p>
        )}
        {product.description && (
          <p className="text-[13px] text-[#3a3a3a] leading-relaxed mb-4 line-clamp-3">{product.description}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-3">
          <span className="font-spectral text-2xl tracking-tight">${product.price_usd.toFixed(2)}</span>
          <Button
            className="rounded-full bg-ink hover:bg-crimson text-white h-9 px-4 text-[13px]"
            onClick={() => setOpen(true)}
            data-testid={`food-buy-${product.id}`}
          >
            Pre-order <ArrowRight size={13} weight="bold" className="ml-1" />
          </Button>
        </div>
      </LuxuryCard>
      <FoodCheckoutDialog product={product} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function FoodCheckoutDialog({ product, open, onClose }) {
  const [form, setForm] = useState({
    quantity: 1,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    delivery_address: "",
    delivery_zip: "",
    delivery_notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [zone, setZone] = useState(null);
  const [zoneStatus, setZoneStatus] = useState(null); // null | "checking" | "ok" | "blocked"
  const [fulfillment, setFulfillment] = useState("delivery"); // "delivery" | "pickup"

  // Load zone config when dialog opens. Allowed ZIPs are public so the
  // buyer-facing UI can pre-validate without spamming the checkout
  // endpoint with bad submits.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/armstrong/food/delivery-zone")
      .then(({ data }) => { if (!cancelled) setZone(data); })
      .catch(() => { if (!cancelled) setZone({ zone_enforced: false }); });
    return () => { cancelled = true; };
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live eligibility check fires as the buyer finishes typing their
  // ZIP. We debounce ~400ms; nothing happens if the zone isn't enforced.
  useEffect(() => {
    if (!open || !zone?.zone_enforced) { setZoneStatus(null); return; }
    const z = (form.delivery_zip || "").trim();
    if (z.length < 5) { setZoneStatus(null); return; }
    setZoneStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/armstrong/food/eligibility?zip=${encodeURIComponent(z)}&email=${encodeURIComponent(form.customer_email || "")}`
        );
        setZoneStatus(data.ok ? "ok" : "blocked");
      } catch {
        setZoneStatus(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.delivery_zip, form.customer_email, open, zone]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const isPickup = fulfillment === "pickup";
      const { data } = await api.post("/armstrong/food/checkout", {
        product_id: product.id,
        ...form,
        fulfillment,
        delivery_address: isPickup ? "" : form.delivery_address,
        delivery_zip: isPickup ? "" : form.delivery_zip,
        origin_url: webOrigin(),
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      // Friendly fallback for proxy / Cloudflare timeouts (502/504/520-524)
      // where the response body is HTML, not a JSON `detail` field.
      const detail = typeof err?.response?.data?.detail === "string"
        ? err.response.data.detail
        : null;
      const status = err?.response?.status;
      const isTransient = !err?.response || (status >= 502 && status <= 524);
      const message =
        detail
        || (isTransient
          ? "Our payment processor is slow right now. Please try again in a moment — your card has not been charged."
          : "Checkout failed. Please try again, or email armstrongk9registry@gmail.com if it keeps happening.");
      toast.error(message);
      setBusy(false);
    }
  };

  const isPickup = fulfillment === "pickup";
  const blocked = !isPickup && zoneStatus === "blocked";
  const contactEmail = zone?.contact_email || "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="food-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">{product.name}</DialogTitle>
          <DialogDescription>
            ${product.price_usd.toFixed(2)} × <QuantityInput value={form.quantity} onChange={(n) => update("quantity", n)} min={1} max={20} className="w-16 ml-1" testid="armstrong-product-qty" />
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Your name</Label>
            <Input required value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="food-form-name" />
          </div>
          <div>
            <Label className="text-xs">Email (for receipt)</Label>
            <Input required type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="food-form-email" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input required value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} data-testid="food-form-phone" />
          </div>
          {zone?.pickup_enabled && (
            <div>
              <Label className="text-xs">How would you like to get it?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1" data-testid="food-fulfillment-toggle">
                <button
                  type="button"
                  onClick={() => setFulfillment("delivery")}
                  className={`rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${
                    !isPickup
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-[#6B6B6B] border-[#EAEAEA] hover:border-ink/40"
                  }`}
                  data-testid="food-fulfillment-delivery"
                >
                  Delivery
                </button>
                <button
                  type="button"
                  onClick={() => setFulfillment("pickup")}
                  className={`rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${
                    isPickup
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-[#6B6B6B] border-[#EAEAEA] hover:border-ink/40"
                  }`}
                  data-testid="food-fulfillment-pickup"
                >
                  Pickup
                </button>
              </div>
            </div>
          )}
          {isPickup ? (
            <div
              className="rounded-lg border border-gold/40 bg-gold/5 px-3 py-3 text-[13px] leading-relaxed"
              data-testid="food-pickup-info"
            >
              <p className="font-semibold text-ink">Pickup location</p>
              <p className="text-[#3a3a3a] mt-0.5" data-testid="food-pickup-address">
                {zone?.pickup_address || "4054 Highway 273, Anderson, CA"}
              </p>
              <p className="text-[#6B6B6B] mt-1.5">
                We&rsquo;ll contact you to arrange pickup time.
              </p>
              <p className="text-[#6B6B6B] mt-0.5">
                Pickup hours: {zone?.pickup_hours || "Contact seller"}
              </p>
            </div>
          ) : (
          <>
          <div>
            <Label className="text-xs">Delivery address</Label>
            <Textarea required value={form.delivery_address} onChange={(e) => update("delivery_address", e.target.value)} rows={2} data-testid="food-form-address" />
          </div>
          <div>
            <Label className="text-xs">
              Delivery ZIP code
              {zone?.zone_enforced && (
                <span className="ml-1 text-[10px] text-[#9A9A9A]">
                  (we deliver locally{zone?.zone_help_text ? ` — ${zone.zone_help_text}` : ""})
                </span>
              )}
            </Label>
            <Input
              required
              inputMode="numeric"
              pattern="[0-9-]*"
              maxLength={10}
              value={form.delivery_zip}
              onChange={(e) => update("delivery_zip", e.target.value.replace(/[^0-9-]/g, ""))}
              placeholder="19103"
              className={
                zoneStatus === "blocked"
                  ? "border-crimson focus-visible:ring-crimson"
                  : zoneStatus === "ok"
                  ? "border-emerald-500"
                  : ""
              }
              data-testid="food-form-zip"
            />
            {zoneStatus === "checking" && (
              <p className="text-[11px] text-[#9A9A9A] mt-1">Checking delivery zone…</p>
            )}
            {zoneStatus === "ok" && (
              <p className="text-[11px] text-emerald-700 mt-1" data-testid="zone-ok-msg">
                ✓ You&rsquo;re in our delivery zone.
              </p>
            )}
            {blocked && (
              <div
                className="mt-2 rounded-lg border border-crimson/30 bg-crimson/5 px-3 py-2 text-[12px] text-crimson leading-relaxed"
                data-testid="zone-blocked-msg"
              >
                Sorry — we don&rsquo;t deliver dog food to this ZIP yet.
                {contactEmail && (
                  <>
                    {" "}Email{" "}
                    <a href={`mailto:${contactEmail}?subject=${encodeURIComponent("Dog food delivery exception request")}`}
                       className="underline font-semibold">
                      {contactEmail}
                    </a>{" "}
                    if you&rsquo;d like to arrange a special delivery.
                  </>
                )}
              </div>
            )}
          </div>
          </>
          )}
          <div>
            <Label className="text-xs">{isPickup ? "Notes (optional)" : "Delivery notes (optional)"}</Label>
            <Textarea value={form.delivery_notes} onChange={(e) => update("delivery_notes", e.target.value)} rows={2} placeholder={isPickup ? "Anything we should know before pickup" : "Gate code, leave with porch, etc."} />
          </div>
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            Orders are non-refundable once placed. If something&rsquo;s wrong with your {isPickup ? "order" : "delivery"}, message us and we&rsquo;ll make it right.
          </p>
          <DialogFooter>
            <Button type="submit" disabled={busy || blocked} className="rounded-full bg-gold-gradient text-ink font-semibold h-11 w-full disabled:opacity-50" data-testid="food-form-submit">
              {busy ? "Loading…" : blocked ? "Outside delivery zone" : `Pay $${(product.price_usd * form.quantity).toFixed(2)} for ${form.quantity} ${form.quantity === 1 ? "item" : "items"} →`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================== MerchCard
function MerchCard({ product }) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const img = product.images?.[0]?.src;
  const customizable = !!product.customizable;
  return (
    <>
      <LuxuryCard interactive className="p-3 flex flex-col relative" data-testid={`merch-card-${product.printify_id}`}>
        {customizable && (
          <span
            className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-gold-gradient text-ink text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2 py-0.5 shadow-sm"
            data-testid={`merch-customizable-badge-${product.printify_id}`}
          >
            <PaintBrush size={10} weight="fill" /> Personalize
          </span>
        )}
        {img ? (
          <img src={img} alt={product.title} className="w-full aspect-square object-cover rounded-md mb-3" />
        ) : (
          <div className="w-full aspect-square rounded-md bg-cream flex items-center justify-center mb-3">
            <TShirt size={42} weight="duotone" className="text-gold" />
          </div>
        )}
        <h3 className="font-spectral text-[15px] leading-tight tracking-tight mb-1 line-clamp-2">{product.title}</h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <span className="font-spectral text-lg tracking-tight">{fmtUSD(product.min_price_cents)}</span>
          <div className="flex gap-1.5">
            {customizable && (
              <Button
                size="sm"
                className="rounded-full text-[12px] h-8 px-3 bg-gold-gradient text-ink font-semibold border-0"
                onClick={() => setCustomOpen(true)}
                data-testid={`merch-customize-${product.printify_id}`}
              >
                Customize
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-full text-[12px] h-8 px-3" onClick={() => setOpen(true)} data-testid={`merch-buy-${product.printify_id}`}>
              Shop
            </Button>
          </div>
        </div>
      </LuxuryCard>
      <MerchCheckoutDialog product={product} open={open} onClose={() => setOpen(false)} />
      <CustomOrderRequestDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        productReference={`${product.title} (${product.printify_id})`}
      />
    </>
  );
}

function MerchCheckoutDialog({ product, open, onClose }) {
  const variants = product.variants || [];
  const [variantId, setVariantId] = useState(variants[0]?.id || null);
  const [form, setForm] = useState({
    quantity: 1,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "US",
  });
  const [busy, setBusy] = useState(false);
  const variant = variants.find((v) => v.id === parseInt(variantId));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!variantId) { toast.error("Select a variant"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/armstrong/merch/checkout", {
        product_id: product.printify_id,
        variant_id: parseInt(variantId),
        ...form,
        origin_url: webOrigin(),
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="merch-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-xl line-clamp-2">{product.title}</DialogTitle>
          <DialogDescription>
            {variant ? `${fmtUSD(variant.price_cents)} · ${variant.title}` : "Pick a variant"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Variant</Label>
            <select
              required
              value={variantId || ""}
              onChange={(e) => setVariantId(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="merch-form-variant"
            >
              {variants.map((v) => (
                <option key={v.id} value={v.id}>{v.title} — {fmtUSD(v.price_cents)}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Quantity</Label>
              <QuantityInput value={form.quantity} onChange={(n) => update("quantity", n)} min={1} max={10} className="w-full" testid="armstrong-variant-qty" />
            </div>
            <div>
              <Label className="text-xs">Full name</Label>
              <Input required value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="merch-form-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input required type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="merch-form-email" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Address line 1</Label>
            <Input required maxLength={35} value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} data-testid="merch-form-addr1" />
          </div>
          <div>
            <Label className="text-xs">Address line 2 (optional)</Label>
            <Input maxLength={30} value={form.address_line2} onChange={(e) => update("address_line2", e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">City</Label>
              <Input required value={form.city} onChange={(e) => update("city", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">State</Label>
              <Input required maxLength={2} value={form.state} onChange={(e) => update("state", e.target.value.toUpperCase())} placeholder="GA" />
            </div>
            <div>
              <Label className="text-xs">ZIP</Label>
              <Input required value={form.zip_code} onChange={(e) => update("zip_code", e.target.value)} />
            </div>
          </div>
          <p className="text-[11px] text-[#6B6B6B]">+$5.99 shipping. Printify ships within 5–7 business days. Print-on-demand orders are non-refundable once submitted to the print partner.</p>
          <DialogFooter>
            <Button type="submit" disabled={busy || !variantId} className="rounded-full bg-gold-gradient text-ink font-semibold h-11 w-full" data-testid="merch-form-submit">
              {busy ? "Loading…" : `Pay ${variant ? fmtUSD(variant.price_cents * form.quantity + 599) : "—"} for ${form.quantity} ${form.quantity === 1 ? "item" : "items"} →`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================== CustomMerchCallout
/**
 * Inline strip below the merch section header that announces the
 * personalize-this option. Visible only when at least one merch item
 * is flagged customizable (otherwise we hide it to avoid promising a
 * service we can't deliver).
 */
function CustomMerchCallout({ count }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className="mb-8 rounded-2xl border border-gold-dark/40 bg-[#FBF7EC] px-5 py-4 flex items-start gap-4 flex-wrap"
        data-testid="custom-merch-callout"
      >
        <div className="p-2.5 rounded-full bg-gold-gradient flex-shrink-0">
          <PaintBrush size={20} weight="fill" className="text-ink" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark">
            Personalize it
          </p>
          <p className="font-spectral text-xl text-ink leading-tight mt-1">
            We can put your name, photo, or dog&rsquo;s name on {count} of our designs.
          </p>
          <p className="text-[12.5px] text-[#6B6B6B] mt-1.5 leading-relaxed">
            Pick the item, tell us what you want, and we&rsquo;ll send you a mockup.
            You only pay once you&rsquo;ve approved the design.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="rounded-full bg-ink hover:bg-crimson text-white h-10 px-5"
          data-testid="custom-merch-open-btn"
        >
          Request a custom item <ArrowRight size={13} weight="bold" className="ml-1.5" />
        </Button>
      </div>
      <CustomOrderRequestDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// ============================================================== CustomOrderRequestDialog
/**
 * Shared modal — buyer fills name/email/description/quantity/target
 * date. Posts to `POST /armstrong/custom/request`. We redirect to the
 * `/armstrong/custom/:order_id` status page so the buyer can poll
 * progress (and pay once the admin sends a mockup + price).
 *
 * `productReference` is set when the dialog is opened from a specific
 * merch card; otherwise blank (general inquiry from the callout).
 */
function CustomOrderRequestDialog({ open, onClose, productReference }) {
  const initial = {
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    product_reference: productReference || "",
    description: "",
    quantity: 1,
    desired_by: "",
    inspiration_url: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...initial, product_reference: productReference || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productReference]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if ((form.description || "").trim().length < 10) {
      toast.error("Please describe your custom design in a bit more detail.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        customer_phone: form.customer_phone || null,
        product_reference: form.product_reference || null,
        desired_by: form.desired_by || null,
        inspiration_url: form.inspiration_url || null,
      };
      const { data } = await api.post("/armstrong/custom/request", payload);
      toast.success("Request sent — we'll email you a mockup within 5 business days.");
      onClose();
      if (data?.id) {
        // Soft-navigate to the status page so the buyer has a stable
        // URL to come back to (also copies easily to share with us).
        window.location.href = `/armstrong/custom/${data.id}`;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="custom-order-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
            <PaintBrush size={22} weight="duotone" className="text-gold-dark" />
            Request a custom item
          </DialogTitle>
          <DialogDescription>
            Tell us what you want on it. We&rsquo;ll send a mockup &mdash; you only pay once you approve.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Your name</Label>
            <Input required value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="custom-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input required type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="custom-form-email" />
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} placeholder="(555) 555-5555" />
            </div>
          </div>
          {productReference ? (
            <div className="rounded-lg border border-[#E8E0CC] bg-cream/60 px-3 py-2 text-[12px] text-[#3a3a3a]" data-testid="custom-form-product-ref">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold-dark">Base item</span>
              <div className="font-medium text-ink mt-0.5">{productReference}</div>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Base item (optional)</Label>
              <Input
                value={form.product_reference}
                onChange={(e) => update("product_reference", e.target.value)}
                placeholder="Hoodie, T-shirt, leash, …"
              />
            </div>
          )}
          <div>
            <Label className="text-xs">What do you want on it?</Label>
            <Textarea
              required
              rows={4}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Dog's name on the back, paw print over the heart, navy + gold colors, etc."
              data-testid="custom-form-description"
            />
            <p className="text-[10.5px] text-[#9A9A9A] mt-1">Minimum 10 characters. The more specific, the faster we can mock it up.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Quantity</Label>
              <QuantityInput
                value={form.quantity}
                onChange={(n) => update("quantity", n)}
                min={1}
                max={200}
                className="w-full"
                testid="custom-form-quantity"
              />
            </div>
            <div>
              <Label className="text-xs">Need it by (optional)</Label>
              <Input value={form.desired_by} onChange={(e) => update("desired_by", e.target.value)} placeholder="Dec 20" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Inspiration link (optional)</Label>
            <Input
              type="url"
              value={form.inspiration_url}
              onChange={(e) => update("inspiration_url", e.target.value)}
              placeholder="https://..."
            />
          </div>
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            We&rsquo;ll email a mockup within 5 business days. No payment is collected until you approve the design and the price.
          </p>
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              className="rounded-full bg-gold-gradient text-ink font-semibold h-11 w-full"
              data-testid="custom-form-submit"
            >
              {busy ? "Sending…" : "Send my request →"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================== CustomOrdersHeroStrip
/**
 * Always-visible hero strip directly under the main Armstrong hero.
 * Promotes the personalization service whether or not specific Printify
 * items are flagged customizable — buyers learn we do custom work
 * before they even hit the merch grid. Clicking either CTA opens the
 * full request dialog (no specific product preselected).
 */
function CustomOrdersHeroStrip() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <section
        id="custom"
        className="relative bg-gradient-to-br from-[#FBF7EC] via-[#F6EFD8] to-[#FBF7EC] py-12 md:py-14 border-y border-gold-dark/30 overflow-hidden"
        data-testid="armstrong-custom-hero"
      >
        <div
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #C9A227 0%, transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, #C1272D 0%, transparent 70%)" }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-3 flex items-center gap-1.5">
              <PaintBrush size={11} weight="fill" /> Custom orders
            </p>
            <h2 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.05] text-ink">
              Want your dog&rsquo;s name<br className="hidden md:block" /> on a hoodie?
            </h2>
            <p className="mt-4 text-[15px] md:text-[16px] leading-relaxed text-[#3a3a3a] max-w-xl">
              We&rsquo;ll mock up your idea on any of our items &mdash; names, photos, dates, dog&rsquo;s portrait, your kennel colors.
              You only pay once you&rsquo;ve approved the design.
            </p>
            <div className="mt-7 flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => setOpen(true)}
                className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-7 font-semibold shadow-[0_8px_24px_rgba(14,14,14,0.25)]"
                data-testid="custom-hero-request-btn"
              >
                Start a custom order <ArrowRight size={15} weight="bold" className="ml-2" />
              </Button>
              <a href="#merch" className="text-[13px] font-semibold text-ink/70 hover:text-ink underline-offset-4 hover:underline">
                or browse personalize-ready items &rarr;
              </a>
            </div>
          </div>
          <div className="hidden md:flex flex-col gap-3 ml-auto w-full max-w-sm">
            <CustomStep n="1" title="Tell us what you want" body="Names, photos, colors, sizes — we'll work from your description." />
            <CustomStep n="2" title="We send you a mockup" body="Usually within 5 business days. Free, no commitment." />
            <CustomStep n="3" title="Approve & pay" body="Secure Stripe checkout. Ships in 7–10 business days." />
          </div>
        </div>
      </section>
      <CustomOrderRequestDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function CustomStep({ n, title, body }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-white/60 backdrop-blur-sm border border-gold-dark/20 p-3.5">
      <span className="flex-shrink-0 h-8 w-8 rounded-full bg-gold-gradient text-ink flex items-center justify-center text-[13px] font-bold shadow-sm">
        {n}
      </span>
      <div>
        <p className="font-spectral text-[15px] leading-tight text-ink">{title}</p>
        <p className="text-[12px] text-[#6B6B6B] mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// ============================================================== PermanentCustomOrdersCard
/**
 * Sits next to the "Armstrong apparel" merch section header. Always
 * visible (whether or not items are flagged customizable). Acts as a
 * second entry point in case buyers arrive at the merch grid before
 * seeing the hero strip.
 */
function PermanentCustomOrdersCard({ customCount }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <LuxuryCard
        className="p-6 flex flex-col justify-between bg-gradient-to-br from-ink via-[#1a1a1a] to-[#2a1f0a] text-white relative overflow-hidden"
        data-testid="permanent-custom-orders-card"
      >
        <div
          className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full opacity-25 blur-2xl"
          style={{ background: "radial-gradient(circle, #C9A227 0%, transparent 70%)" }}
        />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-gold-gradient px-3 py-1 mb-3">
            <PaintBrush size={11} weight="fill" className="text-ink" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink">
              Personalize it
            </span>
          </div>
          <h3 className="font-spectral text-2xl leading-tight">
            We make custom items.
          </h3>
          <p className="text-[13px] text-white/70 mt-2 leading-relaxed">
            Any of our designs can be personalized &mdash; dog&rsquo;s name, photo, colors, dates.
            {customCount > 0 && <> {customCount} item{customCount === 1 ? "" : "s"} are personalize-ready right now.</>}
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="relative mt-5 rounded-full bg-gold-gradient hover:opacity-90 text-ink font-semibold h-11 px-5 border-0 w-full sm:w-auto"
          data-testid="permanent-custom-request-btn"
        >
          Request a custom item <ArrowRight size={14} weight="bold" className="ml-1.5" />
        </Button>
      </LuxuryCard>
      <CustomOrderRequestDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
